import { IElement, Question } from '@psyomics/surveyjs-core';
import { cn } from '@shadcn/lib/utils';
import { useTranslation } from 'react-i18next';

import { capitaliseFirstChar } from '../../../../../../utils/capitalise-first-char';
import { truncateText } from '../../../../../../utils/truncate-text';
import { Badge } from '../../../../../components/atoms/badge/badge';
import { Text } from '../../../../../components/atoms/text/text';
import { useIndicator } from '../../../context/indicators';
import { useAllRespondentTypes, useAnswersForRespondentType } from '../../../context/render-context';
import { Answers, CONTENT_QUESTION_TYPES, FREE_TEXT_QUESTION_TYPES } from '../../../survey-types';
import { ConditionalAnswer } from '../../../utils/surveys/conditional-answers';
import { isAnswerTruncated } from '../../../utils/surveys/questions';
import { getAnswerLabel, shouldShowNotCompleted } from '../../../utils/surveys/report-labels';
import { ReadMoreLink } from '../composite-qa-block/elements/read-more-link';
import { WithIndicator } from '../indicators/with-indicator';

function generateFormattedAnswer(displayText: string, truncateTo: number, prependValue?: string | number) {
  const text = truncateText(capitaliseFirstChar(displayText), truncateTo);
  return prependValue ? `${prependValue} - ${text}` : text;
}

function isQuestion(element: IElement): element is Question {
  return element.isQuestion && !CONTENT_QUESTION_TYPES.includes(element.getType());
}

const useAnswerData = ({
  answers,
  question,
  truncateTo,
  displayAnswerValues,
}: {
  answers: Answers;
  question: Question;
  truncateTo: number;
  displayAnswerValues?: boolean;
}) => {
  const conditionalAnswer = new ConditionalAnswer(question, answers[question.name]);
  const answersToShow = Array.isArray(conditionalAnswer.answer)
    ? conditionalAnswer.answer
    : ([conditionalAnswer.answer] as const);

  const shouldPrependValue = !FREE_TEXT_QUESTION_TYPES.includes(question.getType()) && displayAnswerValues;

  const processedAnswers = answersToShow.map((answer, idx) => {
    const isNotShowQuestion = ConditionalAnswer.isNotShownValue(answer);
    const isSkipped = ConditionalAnswer.isSkippedValue(answer);

    let formattedAnswer = '';
    let originalAnswer = '';
    if (!isNotShowQuestion && !isSkipped) {
      const displayText = question.getDisplayValue(false, answer);
      formattedAnswer = generateFormattedAnswer(displayText, truncateTo, shouldPrependValue ? answer : undefined);
      originalAnswer = displayText;
    }

    return {
      formattedAnswer,
      isNotShowQuestion,
      isSkipped,
      key: `${question.name}-${idx}`,
      originalAnswer,
    };
  });

  return processedAnswers;
};

const AnswerElement = ({
  answers,
  answersOnly,
  question,
  truncateTo,
  displayAnswerValues,
  respondentType,
  link,
  externalLink,
}: {
  readonly answers: Answers;
  readonly answersOnly?: boolean;
  readonly truncateTo: number;
  readonly question: Question;
  readonly displayAnswerValues?: boolean;
  readonly respondentType: string;
  readonly link?: string;
  readonly externalLink?: string;
}) => {
  const processedAnswers = useAnswerData({ answers, question, truncateTo, displayAnswerValues });
  const respondentTypes = useAllRespondentTypes();
  const currentAnswers = useAnswersForRespondentType(respondentType);
  const multipleRespondents = respondentTypes.length > 1;
  const isNotCompleted = shouldShowNotCompleted(multipleRespondents, currentAnswers);

  return (
    <div className="mt-2 first:mt-0 flex flex-col gap-2">
      {!answersOnly && question.title && <Text>{question.title}</Text>}
      <WithIndicator displayIndicator respondentType={respondentType}>
        <div className="flex flex-row items-center gap-1 flex-wrap">
          {processedAnswers.map(({ formattedAnswer, originalAnswer, isNotShowQuestion, isSkipped, key }) => (
            <AnswerValue
              answer={formattedAnswer}
              externalLink={externalLink}
              key={key}
              link={link}
              originalAnswer={originalAnswer}
              question={question}
              questionNotCompleted={isNotCompleted}
              questionNotShown={isNotShowQuestion}
              questionSkipped={isSkipped}
              respondentType={respondentType}
              truncateTo={truncateTo}
            />
          ))}
        </div>
      </WithIndicator>
    </div>
  );
};

const FreeTextAnswer = ({
  answerText,
  indicator,
  questionNotShown,
  questionSkipped,
  originalAnswer,
  linkTo,
  wasTruncated,
}: {
  readonly answerText: string;
  readonly indicator: ReturnType<typeof useIndicator>;
  readonly questionNotShown: boolean;
  readonly questionSkipped: boolean;
  readonly originalAnswer: string;
  readonly linkTo?: string;
  readonly wasTruncated: boolean;
}) => (
  <>
    <Text
      className={
        questionNotShown || questionSkipped
          ? 'free-text-inactive'
          : 'ph-no-capture border-l-3 border-primary-500 pl-1 grow text-typography italic'
      }
      style={indicator ? { borderLeftColor: indicator.colour } : undefined}
    >
      {questionNotShown || questionSkipped ? answerText : `"${answerText}"`}
    </Text>
    {!questionNotShown && !questionSkipped && (
      <ReadMoreLink answer={originalAnswer} linkTo={linkTo} wasTruncated={wasTruncated} />
    )}
  </>
);

const AnswerValue = ({
  question,
  answer,
  respondentType,
  questionNotShown,
  questionSkipped,
  questionNotCompleted,
  link,
  externalLink,
  originalAnswer,
  truncateTo,
}: {
  readonly respondentType: string;
  readonly question: Question;
  readonly answer: string;
  readonly questionNotShown: boolean;
  readonly questionSkipped: boolean;
  readonly questionNotCompleted: boolean;
  readonly link?: string;
  readonly externalLink?: string;
  readonly originalAnswer: string;
  readonly truncateTo: number;
}) => {
  const indicator = useIndicator(respondentType);
  const { t } = useTranslation();
  const wasTruncated = isAnswerTruncated(answer, truncateTo);
  const label = getAnswerLabel(questionNotCompleted, questionNotShown, questionSkipped);
  const answerText = label ? t(label) : answer;

  return (
    <div className="flex flex-row">
      {FREE_TEXT_QUESTION_TYPES.includes(question.getType()) ? (
        <FreeTextAnswer
          answerText={answerText}
          indicator={indicator}
          linkTo={externalLink || link}
          originalAnswer={originalAnswer}
          questionNotShown={questionNotShown}
          questionSkipped={questionSkipped}
          wasTruncated={wasTruncated}
        />
      ) : (
        <Badge appearance={indicator ? indicator.appearance : 'secondary'} className="text-typography">
          {answerText}
        </Badge>
      )}
    </div>
  );
};

const QuestionAnswerBlock = ({
  answers,
  answersOnly,
  displayQuestionNumbers = false,
  displayAnswerValues = false,
  elements,
  truncateTo,
  respondentType,
  link,
  externalLink,
}: {
  readonly answers: Answers;
  readonly answersOnly?: boolean;
  readonly displayQuestionNumbers?: boolean;
  readonly displayAnswerValues?: boolean;
  readonly elements: IElement[];
  readonly truncateTo: number;
  readonly respondentType: string;
  readonly link?: string;
  readonly externalLink?: string;
}) => (
  <div className={`flex flex-col mt-2 marker:text-typography`} data-testid="question-answers-block-container">
    <ol className={cn({ 'list-decimal ml-3': displayQuestionNumbers })}>
      {elements.map((element) => (
        <li className="mb-2" key={element.name}>
          {isQuestion(element) && (
            <AnswerElement
              answers={answers}
              answersOnly={answersOnly}
              displayAnswerValues={displayAnswerValues}
              externalLink={externalLink}
              link={link}
              question={element}
              respondentType={respondentType}
              truncateTo={truncateTo}
            />
          )}
        </li>
      ))}
    </ol>
  </div>
);

export { QuestionAnswerBlock };
